import {
  Award,
  AwardDto,
  BodyPart,
  CompletedByHustlersResponse,
  CompletedChallenge,
  CompletedProgrammeWorkout,
  CompletedWorkout,
  EducationHubCategory,
  FavWorkout,
  FeaturedWorkout,
  Hustler,
  HustlerConfig,
  HustlerProfile,
  HustlerProgramme,
  Intensity,
  NotCompletedChallenge,
  Price,
  Programme,
  ProgrammeDuration,
  PromoCodeValidation,
  ProgrammeWorkout,
  ProgrammeWorkoutsAvailability,
  RunningSeries,
  RunningSeriesWithWorkouts,
  StartProgrammeParams,
  StartedProgrammesFromLibrary,
  SuggestedSeries,
  StoreSubscription,
  Subscription,
  SubscriptionItem,
  Trainer,
  WeekPromoVideo,
  WeeklyImage,
  Workout,
  WorkoutDurationType,
  WorkoutEquipmentGroup,
  WorkoutEquipmentPiece,
  WorkoutStats,
  WorkoutStyle,
  WorkoutType,
  WorkoutsMeta,
  coreDao,
} from '../dao/core-dao'

// NOTE: These are the style IDs for the Flex Flow and Core Charge workouts.
export const CORE_CHARGE_STYLE_ID = 'fc60f2d6-cce5-42b4-be97-4573c31a940c'
export const FLEX_FLOW_STYLE_ID = '96f43efb-c5de-45ee-bcba-9b69178b8783'

export type WorkoutPreset = 'popular' | 'trending_express' | 'new-workouts' | 'new-workouts-expanded' | 'today'
// Allow-listed sort fields exposed by the GH-774 workout filter API. `popularity` is an
// aggregate (all-time completion count), not a column on `Workout`, so this is a dedicated
// union rather than `keyof Workout`.
export type WorkoutOrderField = 'availableAt' | 'title' | 'popularity'
export interface WorkoutFilter {
  trainerIds?: string[]
  durations?: string[]
  intensities?: string[]
  types?: string[]
  styles?: string[]
  sections?: string[]
  bodyParts?: string[]
  // `equipment` is a subset match (workouts whose equipment is entirely within these groups — the
  // "equipment I have" filter panel). `equipmentContains` is a contains match (workouts that include
  // any of these groups) — used by the browse sections so e.g. "Barbell" shows barbell+dumbbell workouts.
  equipment?: string[]
  equipmentContains?: string[]
  orderField?: WorkoutOrderField
  orderDirection?: 'ASC' | 'DESC'
  hideCompleted?: boolean
  preset?: WorkoutPreset
  page?: number
  pageSize?: number
  // TODO: GRND-901 remove redundant availableAtBefore availableAtAfter once most mobile app users are updated
  availableAtBefore?: number
  availableAtAfter?: number
  date?: string
  view?: string
  pagination?: {
    page: number
    pageSize: number
  }
}

export interface CompletedWorkoutFilter {
  orderField?: string
  orderDirection?: 'ASC' | 'DESC'
  completedAtBefore?: number
  completedAtAfter?: number
  workoutIds?: string[]
}

export const coreService = {
  healthCheck: (): any => {
    return coreDao.healthCheck()
  },
  getWorkouts: async (filter?: WorkoutFilter): Promise<Workout[]> => {
    return coreDao.getWorkouts({ ...filter, view: 'web' })
  },
  getWorkoutsWithMeta: async (filter?: WorkoutFilter): Promise<{ data: Workout[]; meta?: WorkoutsMeta }> => {
    return coreDao.getWorkoutsWithMeta({ ...filter, view: 'web' })
  },
  getSuggestedWorkouts: async (): Promise<Workout[]> => {
    return coreDao.getSuggestedWorkouts()
  },
  getFeaturedWorkouts: async (): Promise<FeaturedWorkout[]> => {
    return coreDao.getFeaturedWorkouts()
  },
  getWorkoutById: async ({ workoutId }: { workoutId: string }): Promise<Workout> => {
    return coreDao.getWorkoutById(workoutId)
  },
  getWorkoutTypes: async (): Promise<WorkoutType[]> => {
    return coreDao.getWorkoutTypes()
  },
  completeWorkout: async ({ workoutId }: { workoutId: string }): Promise<CompletedWorkout> => {
    return coreDao.completeWorkout(workoutId)
  },
  getMe: async (): Promise<Hustler> => {
    return coreDao.getHustlerMe()
  },
  getConfig: async (): Promise<HustlerConfig> => {
    return coreDao.getConfig()
  },
  updateMe: async (params: { profile: HustlerProfile }): Promise<Hustler> => {
    return coreDao.patchHustlerMe(params)
  },
  validatePromoCode: async (code: string): Promise<PromoCodeValidation> => {
    return coreDao.validatePromoCode(code)
  },
  createCheckoutSession: async (
    priceId: string,
    promotionCode?: string,
    options?: { skipErrorInterceptor?: boolean },
  ): Promise<string> => {
    return coreDao.createCheckoutSession(priceId, promotionCode, options)
  },
  createPortalSession: async (): Promise<string> => {
    return coreDao.createPortalSession()
  },
  getMySubscriptions: async (): Promise<StoreSubscription | undefined> => {
    return coreDao.getMySubscriptions()
  },
  getDetailedSubscriptions: async (): Promise<Subscription[]> => {
    return coreDao.getDetailedSubscriptions()
  },
  getMyActiveOrTrialingSubscriptions: async (): Promise<Subscription[]> => {
    const mySubscriptions = await coreService.getDetailedSubscriptions()
    return mySubscriptions.filter((sub) => {
      if (!sub.cancelAt && !sub.canceledAt && sub.status === 'trialing') return true
      return sub.status === 'active'
    })
  },

  getMyActiveOrTrialingSubscriptionItems: async (): Promise<SubscriptionItem[]> => {
    const mySubscriptions = await coreService.getMyActiveOrTrialingSubscriptions()
    return mySubscriptions
      .map((sub) => {
        return sub.items
      })
      .flatMap((item: SubscriptionItem[]) => {
        return item
      })
  },
  getPrices: async (): Promise<Price[]> => {
    return coreDao.getPrices()
  },
  getTrainers: async (): Promise<Trainer[]> => {
    return coreDao.getTrainers()
  },
  getTrainer: async (params: { trainerId: string }): Promise<Trainer> => {
    return coreDao.getTrainer(params)
  },
  getCompletedWorkouts: async (params?: { filter?: CompletedWorkoutFilter }): Promise<CompletedWorkout[]> => {
    return coreDao.getCompletedWorkouts(params)
  },
  getLegalTermsAndConditions: async (): Promise<string> => {
    return coreDao.getLegalTermsAndConditions()
  },
  getLegalPrivacyPolicy: async (): Promise<string> => {
    return coreDao.getLegalPrivacyPolicy()
  },
  getWeeklyImage: async (params: { dayInWeek: number }): Promise<WeeklyImage> => {
    return coreDao.getWeeklyImage(params)
  },
  getBodyParts(): Promise<BodyPart[]> {
    return coreDao.getBodyParts()
  },
  getWorkoutEquipmentPieces: (): Promise<WorkoutEquipmentPiece[]> => {
    return coreDao.getWorkoutEquipmentPieces()
  },
  getProgrammes: async (): Promise<Programme[]> => {
    return coreDao.getProgrammes()
  },
  getProgrammeById: async (programmeId: string): Promise<Programme> => {
    return coreDao.getProgrammeById(programmeId)
  },
  getIntensity: async (params: { id: string }): Promise<Intensity> => {
    return coreDao.getIntensity(params)
  },
  getProgrammeDuration: async (params: { programmeId: string }): Promise<ProgrammeDuration> => {
    return coreDao.getProgrammeDuration(params)
  },
  listProgrammeIntensities(): Promise<Intensity[]> {
    return coreDao.listProgrammeIntensities()
  },
  getProgrammeEquipmentList: async (params: { programmeId: string }): Promise<WorkoutEquipmentPiece[]> => {
    return coreDao.getProgrammeEquipmentList(params)
  },
  getMyStartedProgrammes: async (): Promise<HustlerProgramme[]> => {
    return coreDao.getMyStartedProgrammes()
  },
  getMyStartedProgrammesFromLibrary: async (params: {
    page: number
    pageSize: number
  }): Promise<StartedProgrammesFromLibrary> => {
    return coreDao.getMyStartedProgrammesFromLibrary(params)
  },
  getMyCompletedProgrammes: async (): Promise<HustlerProgramme[]> => {
    return coreDao.getMyCompletedProgrammes()
  },
  startProgramme: async (params: StartProgrammeParams): Promise<HustlerProgramme> => {
    return coreDao.startProgramme(params)
  },
  getSuggestedSeries: async (): Promise<SuggestedSeries | undefined> => {
    return coreDao.getSuggestedSeries()
  },
  dismissSuggestedSeries: async (programmeId: string): Promise<void> => {
    return coreDao.dismissSuggestedSeries(programmeId)
  },
  cancelMyProgramme: async (programmeId: string): Promise<void> => {
    return coreDao.cancelMyProgramme(programmeId)
  },
  resetSuggestedSeriesFlow: async (): Promise<void> => {
    return coreDao.resetSuggestedSeriesFlow()
  },
  completeTour: async (): Promise<void> => {
    return coreDao.completeTour()
  },
  listWorkoutIntensities: async (): Promise<Intensity[]> => {
    return coreDao.listWorkoutIntensities()
  },
  listRunningSeries: async (): Promise<RunningSeries[]> => {
    return coreDao.listRunningSeries()
  },
  getTrainingSeriesById: async (trainingSeriesId: string): Promise<RunningSeriesWithWorkouts> => {
    return coreDao.getTrainingSeriesById({ trainingSeriesId })
  },
  getMyCurrentMonthWorkoutStats: async (): Promise<WorkoutStats[]> => {
    return coreDao.getMyCurrentMonthWorkoutStats()
  },
  getFavoriteWorkouts: async (): Promise<FavWorkout[]> => {
    return coreDao.getFavoriteWorkouts()
  },
  addToFavorites: async ({ workoutId }: { workoutId: string }): Promise<FavWorkout> => {
    return coreDao.addToFavorites({ workoutId })
  },
  removeFavoriteWorkout: async ({ id }: { id: string }): Promise<void> => {
    return coreDao.removeFavoriteWorkout({ id })
  },
  getMyProgrammeAwards: async (): Promise<AwardDto[]> => {
    return coreDao.getMyProgrammeAwards()
  },
  getCompletedChallenges: async (params?: { limit?: number }): Promise<CompletedChallenge[]> => {
    return coreDao.getCompletedChallenges(params)
  },
  getNotYetCompletedChallenges: async (): Promise<NotCompletedChallenge[]> => {
    return coreDao.getNotYetCompletedChallenges()
  },
  listWorkoutStyles: async (): Promise<WorkoutStyle[]> => {
    return coreDao.listWorkoutStyles()
  },
  getWorkoutStyle: async (workoutStyleId: string): Promise<WorkoutStyle> => {
    return coreDao.getWorkoutStyle({ workoutStyleId })
  },
  listEducationHubCategories: async (): Promise<EducationHubCategory> => {
    return coreDao.listEducationHubCategories()
  },

  listWorkoutDurations: async (): Promise<WorkoutDurationType[]> => {
    return coreDao.listWorkoutDurations()
  },
  listWorkoutEquipmentGroups: async (): Promise<WorkoutEquipmentGroup[]> => {
    return coreDao.listWorkoutEquipmentGroups()
  },
  getHustlerProgramme: async (programmeId: string): Promise<HustlerProgramme> => {
    return coreDao.getHustlerProgramme({ programmeId })
  },
  getProgrammeWorkouts: async (programmeId: string): Promise<ProgrammeWorkout[]> => {
    return coreDao.getProgrammeWorkouts(programmeId)
  },
  getMyCompletedProgrammeWorkoutsFromProgramme: async (programmeId: string): Promise<CompletedProgrammeWorkout[]> => {
    return coreDao.getMyCompletedProgrammeWorkoutsFromProgramme(programmeId)
  },
  getProgrammeWorkoutsAvailability: async (programmeId: string): Promise<ProgrammeWorkoutsAvailability[]> => {
    return coreDao.getProgrammeWorkoutsAvailability({ programmeId })
  },
  getAllAvailableProgrammeAwards: async (programmeId: string): Promise<Award[]> => {
    return coreDao.getAllAvailableProgrammeAwards({ programmeId })
  },
  getMyProgrammeAwardsByProgramme: async (programmeId: string): Promise<AwardDto[]> => {
    return coreDao.getMyProgrammeAwardsByProgramme(programmeId)
  },
  completeProgrammeWorkout: async (params: {
    programmeId: string
    programmeWorkoutId: string
    maxHeartRate: number
    calories: number
    averageHeartRate: number
    hustleScore: number
  }): Promise<CompletedProgrammeWorkout> => {
    return coreDao.completeProgrammeWorkout(params)
  },
  getWeekPromoVideo: async (): Promise<WeekPromoVideo> => {
    return coreDao.getWeekPromoVideo()
  },
  getCoreChargeWorkouts: async (params?: { page?: number; pageSize?: number }): Promise<Workout[]> => {
    return coreDao.getWorkouts({
      styles: [CORE_CHARGE_STYLE_ID],
      view: 'web',
      ...(params?.page !== undefined && { page: params.page }),
      ...(params?.pageSize !== undefined && { pageSize: params.pageSize }),
    })
  },
  getFlexFlowWorkouts: async (params?: { page?: number; pageSize?: number }): Promise<Workout[]> => {
    return coreDao.getWorkouts({
      styles: [FLEX_FLOW_STYLE_ID],
      view: 'web',
      ...(params?.page !== undefined && { page: params.page }),
      ...(params?.pageSize !== undefined && { pageSize: params.pageSize }),
    })
  },
  getWorkoutCompletedByHustlers: async (params: {
    workoutId: string
    page?: number
    pageSize?: number
    includeMetrics?: boolean
    includeCompletedAt?: boolean
    includeCompletedWorkoutCount?: boolean
  }): Promise<CompletedByHustlersResponse> => {
    const include: string[] = []
    if (params.includeMetrics) include.push('metrics')
    if (params.includeCompletedAt) include.push('completedAt')
    if (params.includeCompletedWorkoutCount) include.push('completedWorkoutCount')

    return coreDao.getWorkoutCompletedByHustlers({
      workoutId: params.workoutId,
      page: params.page,
      pageSize: params.pageSize,
      include,
    })
  },
}
